"use client";

import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { cn } from '../../lib/utils';
import { Button } from '../ui/button';
import StoryGenieLogoSvg from '../../assets/images/storygenie-logo.svg';

interface PublishLoaderProps {
  title?: string;
  subtitle?: string;
  successTitle?: string;
  successSubtitle?: string;
  errorTitle?: string;
  errorSubtitle?: string;
  size?: 'sm' | 'md' | 'lg';
  variant?: 'overlay' | 'inline';
  isVisible?: boolean;
  isSuccess?: boolean;
  isError?: boolean;
  className?: string;
  onSuccessComplete?: () => void;
  onErrorComplete?: () => void;
}

export default function PublishLoader({
  title = 'Publishing your stories...',
  subtitle = 'Your ideas are taking flight to Jira! ✨',
  successTitle = 'Published Successfully!',
  successSubtitle = 'Your work items are now live in Jira 🎉',
  errorTitle = 'Uh oh! Publishing issues failed',
  errorSubtitle = 'An unknown error occurred while publishing to Jira. Please try again soon.',
  size = 'lg',
  variant = 'overlay',
  isVisible = true,
  isSuccess = false,
  isError = false,
  className,
  onSuccessComplete,
  onErrorComplete,
}: PublishLoaderProps) {
  const sizeConfig = {
    sm: {
      container: 'size-24',
      logoSize: 'size-14',
      titleClass: 'text-sm font-medium',
      subtitleClass: 'text-xs',
      spacing: 'space-y-2',
      maxWidth: 'max-w-48',
    },
    md: {
      container: 'size-36',
      logoSize: 'size-20',
      titleClass: 'text-base font-medium',
      subtitleClass: 'text-sm',
      spacing: 'space-y-3',
      maxWidth: 'max-w-56',
    },
    lg: {
      container: 'size-44',
      logoSize: 'size-24',
      titleClass: 'text-xl font-semibold',
      subtitleClass: 'text-base',
      spacing: 'space-y-4',
      maxWidth: 'max-w-96',
    },
  };

  const config = sizeConfig[size];

  // StoryGenie brand purple colors
  const brandPrimary = '#8b5cf6';
  const brandSecondary = '#a78bfa';
  const brandTertiary = '#c4b5fd';

  // Success green colors
  const successPrimary = '#22c55e';
  const successSecondary = '#4ade80';
  const successTertiary = '#86efac';

  // Error red colors
  const errorPrimary = '#ef4444';
  const errorSecondary = '#f87171';
  const errorTertiary = '#fca5a5';

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className={
            variant === 'overlay'
              ? 'fixed inset-0 z-[9999] flex items-center justify-center'
              : 'relative flex items-center justify-center w-full'
          }
        >
          {variant === 'overlay' && (
            <motion.div
              className="absolute inset-0 bg-black/20 dark:bg-black/60"
              style={{ backdropFilter: 'blur(8px)' }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.5 }}
            />
          )}
          
          {/* Loader Content Card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95, y: -20 }}
            transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            className={cn(
              'relative flex flex-col items-center justify-center gap-8 p-10 rounded-2xl',
              'bg-[var(--sg-bg-primary,#ffffff)] dark:bg-[#1f1f21]',
              'shadow-2xl border border-[var(--sg-border-default,#e0e0e4)] dark:border-white/10',
              'w-[320px] h-[380px]', // Fixed dimensions to prevent size changes during transitions
              className
            )}
          >
            <AnimatePresence mode="wait">
              {!isSuccess && !isError ? (
                /* Loading State */
                <motion.div
                  key="loading"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0, scale: 0.8 }}
                  transition={{ duration: 0.3 }}
                  className="flex flex-col items-center gap-8 w-[280px]"
                >
                  {/* Animated Loader Rings with Logo */}
                  <motion.div
                    className={cn('relative', config.container)}
                    animate={{ scale: [1, 1.02, 1] }}
                    transition={{
                      duration: 4,
                      repeat: Number.POSITIVE_INFINITY,
                      ease: [0.4, 0, 0.6, 1],
                    }}
                  >
                    {/* Outermost ring - slowest */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `conic-gradient(from 0deg, transparent 0%, ${brandTertiary} 25%, transparent 50%)`,
                        mask: `radial-gradient(circle, transparent 65%, black 67%, black 70%, transparent 72%)`,
                        WebkitMask: `radial-gradient(circle, transparent 65%, black 67%, black 70%, transparent 72%)`,
                      }}
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 4,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: 'linear',
                      }}
                    />

                    {/* Middle ring - medium speed */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `conic-gradient(from 120deg, transparent 0%, ${brandSecondary} 30%, transparent 60%)`,
                        mask: `radial-gradient(circle, transparent 50%, black 52%, black 58%, transparent 60%)`,
                        WebkitMask: `radial-gradient(circle, transparent 50%, black 52%, black 58%, transparent 60%)`,
                      }}
                      animate={{ rotate: -360 }}
                      transition={{
                        duration: 3,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: 'linear',
                      }}
                    />

                    {/* Inner ring - fastest, most prominent */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `conic-gradient(from 240deg, transparent 0%, ${brandPrimary} 35%, ${brandSecondary} 50%, transparent 70%)`,
                        mask: `radial-gradient(circle, transparent 35%, black 37%, black 45%, transparent 47%)`,
                        WebkitMask: `radial-gradient(circle, transparent 35%, black 37%, black 45%, transparent 47%)`,
                      }}
                      animate={{ rotate: 360 }}
                      transition={{
                        duration: 2,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: 'linear',
                      }}
                    />

                    {/* Center glow pulse */}
                    <motion.div
                      className="absolute inset-[25%] rounded-full"
                      style={{
                        background: `radial-gradient(circle, ${brandPrimary}30 0%, ${brandSecondary}15 50%, transparent 70%)`,
                      }}
                      animate={{
                        scale: [0.8, 1.2, 0.8],
                        opacity: [0.4, 0.7, 0.4],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: 'easeInOut',
                      }}
                    />

                    {/* Center Logo */}
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      animate={{
                        scale: [1, 1.08, 1],
                      }}
                      transition={{
                        duration: 2,
                        repeat: Number.POSITIVE_INFINITY,
                        ease: 'easeInOut',
                      }}
                    >
                      <img 
                        src={StoryGenieLogoSvg} 
                        alt="StoryGenie" 
                        className={cn(config.logoSize, 'object-contain drop-shadow-lg')}
                      />
                    </motion.div>
                  </motion.div>

                  {/* Text Content */}
                  <motion.div
                    className={cn('text-center', config.spacing, config.maxWidth)}
                    initial={{ opacity: 0, y: 16 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.3, duration: 0.6, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <motion.h1
                      className={cn(
                        config.titleClass,
                        'text-[var(--sg-text-primary,#172b4d)] tracking-tight'
                      )}
                    >
                      {title}
                    </motion.h1>
                    <motion.p
                      className={cn(
                        config.subtitleClass,
                        'text-[var(--sg-text-secondary,#5d6b82)] mt-2'
                      )}
                    >
                      {subtitle}
                    </motion.p>
                  </motion.div>
                </motion.div>
              ) : isSuccess ? (
                /* Success State */
                <motion.div
                  key="success"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  className="flex flex-col items-center gap-8 w-[280px]"
                >
                  {/* Success Checkmark with Gradient */}
                  <motion.div
                    className={cn('relative', config.container)}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ 
                      type: 'spring', 
                      stiffness: 200, 
                      damping: 15,
                      delay: 0.1 
                    }}
                  >
                    {/* Outer glow ring */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `radial-gradient(circle, ${successPrimary}40 0%, ${successSecondary}20 50%, transparent 70%)`,
                      }}
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ 
                        scale: [1, 1.3, 1.1], 
                        opacity: [0, 0.8, 0.6] 
                      }}
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                    />
                    
                    {/* Success circle background */}
                    <motion.div
                      className="absolute inset-[15%] rounded-full shadow-lg dark:shadow-none"
                      style={{
                        background: `linear-gradient(135deg, ${successPrimary} 0%, ${successSecondary} 50%, ${successTertiary} 100%)`,
                      }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ 
                        type: 'spring', 
                        stiffness: 300, 
                        damping: 20,
                        delay: 0.2 
                      }}
                    />

                    {/* Checkmark SVG */}
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.4, duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <svg 
                        className="w-1/3 h-1/3 text-white drop-shadow-lg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <motion.path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M5 13l4 4L19 7"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ delay: 0.5, duration: 0.5, ease: 'easeOut' }}
                        />
                      </svg>
                    </motion.div>

                    {/* Particle burst effect */}
                    {[...Array(8)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 rounded-full"
                        style={{
                          background: i % 2 === 0 ? successPrimary : successSecondary,
                          left: '50%',
                          top: '50%',
                        }}
                        initial={{ x: '-50%', y: '-50%', scale: 0, opacity: 1 }}
                        animate={{
                          x: `calc(-50% + ${Math.cos((i * Math.PI * 2) / 8) * 80}px)`,
                          y: `calc(-50% + ${Math.sin((i * Math.PI * 2) / 8) * 80}px)`,
                          scale: [0, 1.5, 0],
                          opacity: [1, 1, 0],
                        }}
                        transition={{ 
                          delay: 0.3, 
                          duration: 0.8, 
                          ease: 'easeOut' 
                        }}
                      />
                    ))}
                  </motion.div>

                  {/* Success Text */}
                  <motion.div
                    className={cn('text-center', config.spacing, config.maxWidth)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <motion.h1
                      className={cn(
                        config.titleClass,
                        'text-[var(--sg-text-primary,#172b4d)] tracking-tight'
                      )}
                    >
                      {successTitle}
                    </motion.h1>
                    <motion.p
                      className={cn(
                        config.subtitleClass,
                        'text-[var(--sg-text-secondary,#5d6b82)] mt-2'
                      )}
                    >
                      {successSubtitle}
                    </motion.p>
                  </motion.div>

                  {/* OK Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.3 }}
                  >
                    <Button
                      variant="primary"
                      onClick={onSuccessComplete}
                      className="h-9 px-6"
                    >
                      OK
                    </Button>
                  </motion.div>
                </motion.div>
              ) : isError ? (
                /* Error State */
                <motion.div
                  key="error"
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  className="flex flex-col items-center gap-8 w-[280px]"
                >
                  {/* Error X with Gradient */}
                  <motion.div
                    className={cn('relative', config.container)}
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ 
                      type: 'spring', 
                      stiffness: 200, 
                      damping: 15,
                      delay: 0.1 
                    }}
                  >
                    {/* Outer glow ring */}
                    <motion.div
                      className="absolute inset-0 rounded-full"
                      style={{
                        background: `radial-gradient(circle, ${errorPrimary}40 0%, ${errorSecondary}20 50%, transparent 70%)`,
                      }}
                      initial={{ scale: 0.5, opacity: 0 }}
                      animate={{ 
                        scale: [1, 1.3, 1.1], 
                        opacity: [0, 0.8, 0.6] 
                      }}
                      transition={{ duration: 0.8, ease: 'easeOut' }}
                    />
                    
                    {/* Error circle background */}
                    <motion.div
                      className="absolute inset-[15%] rounded-full shadow-lg dark:shadow-none"
                      style={{
                        background: `linear-gradient(135deg, ${errorPrimary} 0%, ${errorSecondary} 50%, ${errorTertiary} 100%)`,
                      }}
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ 
                        type: 'spring', 
                        stiffness: 300, 
                        damping: 20,
                        delay: 0.2 
                      }}
                    />

                    {/* X SVG */}
                    <motion.div
                      className="absolute inset-0 flex items-center justify-center"
                      initial={{ scale: 0, opacity: 0 }}
                      animate={{ scale: 1, opacity: 1 }}
                      transition={{ delay: 0.4, duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
                    >
                      <svg 
                        className="w-1/3 h-1/3 text-white drop-shadow-lg" 
                        fill="none" 
                        viewBox="0 0 24 24" 
                        stroke="currentColor"
                        strokeWidth={3}
                      >
                        <motion.path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 18L18 6"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ delay: 0.5, duration: 0.3, ease: 'easeOut' }}
                        />
                        <motion.path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          d="M6 6l12 12"
                          initial={{ pathLength: 0 }}
                          animate={{ pathLength: 1 }}
                          transition={{ delay: 0.65, duration: 0.3, ease: 'easeOut' }}
                        />
                      </svg>
                    </motion.div>

                    {/* Shake effect particles */}
                    {[...Array(6)].map((_, i) => (
                      <motion.div
                        key={i}
                        className="absolute w-2 h-2 rounded-full"
                        style={{
                          background: i % 2 === 0 ? errorPrimary : errorSecondary,
                          left: '50%',
                          top: '50%',
                        }}
                        initial={{ x: '-50%', y: '-50%', scale: 0, opacity: 1 }}
                        animate={{
                          x: `calc(-50% + ${Math.cos((i * Math.PI * 2) / 6) * 70}px)`,
                          y: `calc(-50% + ${Math.sin((i * Math.PI * 2) / 6) * 70}px)`,
                          scale: [0, 1.2, 0],
                          opacity: [1, 1, 0],
                        }}
                        transition={{ 
                          delay: 0.4, 
                          duration: 0.7, 
                          ease: 'easeOut' 
                        }}
                      />
                    ))}
                  </motion.div>

                  {/* Error Text */}
                  <motion.div
                    className={cn('text-center', config.spacing, config.maxWidth)}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.6, duration: 0.5, ease: [0.4, 0, 0.2, 1] }}
                  >
                    <motion.h1
                      className={cn(
                        config.titleClass,
                        'text-[var(--sg-text-primary,#172b4d)] tracking-tight'
                      )}
                    >
                      {errorTitle}
                    </motion.h1>
                    <motion.p
                      className={cn(
                        config.subtitleClass,
                        'text-[var(--sg-text-secondary,#5d6b82)] mt-2'
                      )}
                    >
                      {errorSubtitle}
                    </motion.p>
                  </motion.div>

                  {/* Close Button */}
                  <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.8, duration: 0.3 }}
                  >
                    <Button
                      variant="primary"
                      onClick={onErrorComplete}
                      className="h-9 px-6"
                    >
                      Close
                    </Button>
                  </motion.div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
